import { motion } from 'framer-motion';
import {
  Map,
  Users as UsersIcon,
  TrendingUp,
  Globe,
  Shield,
  CheckCircle,
  ArrowRight,
  Zap,
  AlertTriangle,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function ExternalAssessmentPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const assessmentFrameworks = [
    {
      icon: Shield,
      title: "Porter's Five Forces",
      subtitle: 'Understand Competitive Dynamics',
      description:
        'Analyze the five forces that determine industry profitability and competitive intensity.',
      link: '/external-assessment/porters-five-forces',
      forces: [
        'Threat of New Entrants - How easy is it for competitors to enter your market?',
        'Bargaining Power of Suppliers - How much control do suppliers have over you?',
        'Bargaining Power of Buyers - How much leverage do customers have?',
        'Threat of Substitutes - What alternatives exist to your product/service?',
        'Competitive Rivalry - How intense is competition among existing players?',
      ],
      insight: "Identifies where you have power and where you're vulnerable in your market.",
    },
    {
      icon: Globe,
      title: 'PESTEL Analysis',
      subtitle: 'Map Your External Environment',
      description:
        'Examine macro-environmental factors that impact your organization beyond your control.',
      link: '/external-assessment/pestel-analysis',
      forces: [
        'Political - Government policies, regulations, political stability',
        'Economic - Economic growth, interest rates, inflation, exchange rates',
        'Social - Demographics, lifestyle trends, cultural factors',
        'Technological - Innovation, automation, R&D investment',
        'Environmental - Climate change, sustainability requirements',
        'Legal - Laws, regulations, industry standards',
      ],
      insight: 'Spots opportunities and threats from macro trends before they impact you.',
    },
    {
      icon: TrendingUp,
      title: 'Market Analysis',
      subtitle: 'Size Your Opportunity',
      description: 'Understand your market size, growth rate, and key segments.',
      link: '/external-assessment/market-analysis',
      forces: [
        'Total Addressable Market (TAM) - The full market demand',
        'Serviceable Available Market (SAM) - The portion you can reach',
        'Serviceable Obtainable Market (SOM) - What you can realistically capture',
        'Market Growth Rate - How fast is the market expanding?',
        "Market Trends - What's changing in customer behavior?",
      ],
      insight: 'Validates whether the opportunity justifies your strategic investment.',
    },
    {
      icon: UsersIcon,
      title: 'Competitive Positioning',
      subtitle: 'Know Your Competitors',
      description:
        'Map where you stand relative to competitors and identify differentiation opportunities.',
      link: '/external-assessment/competitive-positioning',
      forces: [
        'Direct Competitors - Who offers similar solutions?',
        'Indirect Competitors - Who solves the same problem differently?',
        'Competitive Advantages - What do competitors do better?',
        'Market Positioning - How are you perceived vs. competitors?',
        'White Space Opportunities - Where is no one competing effectively?',
      ],
      insight: 'Reveals where you can win and where you need to improve or differentiate.',
    },
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Ask better questions',
      description:
        'Use structured prompts to examine the external factors your plan needs to account for.',
    },
    {
      icon: TrendingUp,
      title: 'Review what is changing',
      description:
        'Consider the market shifts that could affect your customers, costs and growth options.',
    },
    {
      icon: Shield,
      title: 'Make assumptions visible',
      description:
        'Identify the uncertainties to monitor and the risks to discuss before committing resources.',
    },
    {
      icon: Zap,
      title: 'Focus your research',
      description:
        'Identify potential customer segments and opportunities to validate with evidence.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'External Assessment' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Map className="w-4 h-4 mr-2" />
            Step 2 of 5
          </span>
        }
        title="Understand the forces shaping your next move."
        subtitle="Look beyond your company to the customers, competitors and market changes that matter. Turn a broad external assessment into focused questions for your strategy."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Explore my market context
        </Link>
      </PageHero>

      {/* Assessment Frameworks */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">See your market from more than one angle.</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Use familiar strategic frameworks to analyze your external environment from multiple
              angles
            </p>
          </motion.div>

          <div className="space-y-16 max-w-6xl mx-auto">
            {assessmentFrameworks.map((framework, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-200"
              >
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-br from-blue-600 to-purple-600 w-20 h-20 rounded-xl flex items-center justify-center shadow-sm">
                      <framework.icon className="w-10 h-10 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold mb-2">{framework.title}</h3>
                    <p className="text-blue-600 font-semibold mb-4 text-lg">{framework.subtitle}</p>
                    <p className="text-gray-700 mb-6 text-lg">{framework.description}</p>

                    {/* Forces/Factors */}
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">What You'll Analyze:</h4>
                      <ul className="space-y-2">
                        {framework.forces.map((force, idx) => (
                          <li key={idx} className="flex items-start">
                            <div className="bg-blue-500 w-1.5 h-1.5 rounded-full mr-3 mt-2 flex-shrink-0" />
                            <span className="text-gray-700">{force}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Insight */}
                    <div className="public-card bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-4">
                      <div className="flex items-start space-x-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Strategic Insight:</h4>
                          <p className="text-sm text-gray-700">{framework.insight}</p>
                        </div>
                      </div>
                    </div>

                    {/* Learn More Link */}
                    <Link
                      to={framework.link}
                      className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                    >
                      Learn More About {framework.title} <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bring the outside world into your decisions. */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Bring the outside world into your decisions.
              </h2>
              <p className="text-xl text-gray-600">
                Even the best internal capabilities mean nothing if market forces work against you.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card text-center bg-white p-6 rounded-xl shadow-sm"
                >
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section public-cta py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">
              Know the market. Now assess your ability to compete.
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Bring what you have learned outside the company into a clear review of your
              capabilities and resources.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/foundation"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                ← Previous: Foundation
              </Link>
              <Link to="/internal-assessment" className="public-button public-button--primary">
                Next: Internal Assessment <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
